import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import Overline from "../components/Overline";
import StatusChip from "../components/StatusChip";
import Badge from "../components/Badge";
import Rail from "../components/Rail";
import { VideoIcon } from "../components/icons";
import CategoryIcon from "../components/categoryIcons";
import { paths } from "../routing/paths";
import { getToday, getParsha, getUpcoming, getDailyShiur } from "../api/redesignApi";
import useLiveStatus from "../api/useLiveStatus";
import { usePlayer } from "../player/PlayerProvider";
import { trackEvent } from "../../analytics";

/*
 * Zone 3 — the rhythm row (Homepage reference): TODAY overline with
 * Hebrew + civil date, three equal daily tiles, the live slot (3 states),
 * and the Coming Up rail (featured double-width card first, Hebrew-lockup
 * fallback for dates without banner art — nothing renders blank).
 */

function DailyTile({ href, kicker, title, meta, ariaLabel, iconKey, onClick }) {
  return (
    <Link
      to={href}
      onClick={onClick}
      aria-label={ariaLabel}
      className="min-w-0 bg-white border border-ic-lightGray rounded-xl p-3 min-h-[72px] flex items-center gap-2.5 no-underline hover:no-underline shadow-ic-raised transition-all duration-150 ease-out hover:shadow-ic-elevated hover:border-ic-borderGray focus-visible:outline-none focus-visible:border-ic-blue focus-visible:shadow-ic-focus-soft"
    >
      {iconKey && <CategoryIcon categoryKey={iconKey} variant="small" size={32} className="hidden sm:block" />}
      <span className="flex flex-col justify-center gap-0.5 min-w-0 flex-1">
        <span className="text-[12px] font-bold leading-4 tracking-[0.5px] uppercase text-ic-blue">{kicker}</span>
        <span className="text-[16px] font-semibold leading-6 text-black">{title}</span>
        {meta && <span className="text-[12px] leading-4 text-ic-slate">{meta}</span>}
      </span>
    </Link>
  );
}

/* Player item for a daily shiur — audio first (it lives quietly in the
 * mini-player), video when that's all the class has. */
function dailyItem(c) {
  const mediaKind = c.audio ? "audio" : "video";
  const media = c.audio || c.video;
  return {
    classId: c.id,
    title: c.title,
    seriesTitle: null,
    positionLabel: null,
    mediaKind,
    src: media.src,
    streamUrl: media.streamUrl,
    poster: mediaKind === "video" ? media.poster : null,
    href: c.slug ? paths.permalink(c.slug) : null,
    durationHint: c.audio?.durationSeconds || 0,
  };
}

/*
 * The daily tiles start today's shiur in the persistent player (mini-player
 * at the bottom) rather than navigating. The daily page stays the fallback:
 * modifier-clicks open it, and so does a day with no playable class.
 */
function useDailyPlay(apiName, fallbackHref) {
  const player = usePlayer();
  const current = useSelector((s) => s.player.current);
  const playing = useSelector((s) => s.player.playing);
  const [classes, setClasses] = useState(null);

  useEffect(() => {
    let cancelled = false;
    getDailyShiur(apiName).then((r) => !cancelled && setClasses(r?.classes || []));
    return () => {
      cancelled = true;
    };
  }, [apiName]);

  const playable = (classes || []).filter((c) => c.audio || c.video).map(dailyItem);
  const first = playable[0];
  const isCurrent = Boolean(first && current) && (current.src || current.streamUrl) === (first.src || first.streamUrl);

  const onClick = (e) => {
    if (!first || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    e.preventDefault();
    try {
      trackEvent({ category: "home", action: "daily_play", label: apiName });
    } catch (_) {}
    if (isCurrent) {
      player.toggle();
      return;
    }
    // Leap-year splits: the remaining parts play next.
    player.setQueue(playable.slice(1));
    player.load({ ...first, href: first.href || fallbackHref }, { autoplay: true });
  };

  return { onClick, playing: isCurrent && playing };
}

function LiveSlot({ status }) {
  if (!status) return null;
  if (status.state === "live") {
    return (
      <a
        href={paths.live()}
        className="bg-ic-error text-white rounded-xl px-4 py-3 min-h-[44px] flex items-center gap-2.5 text-[16px] font-bold leading-6 no-underline box-border hover:no-underline hover:shadow-ic-elevated focus-visible:outline-none focus-visible:shadow-[0px_0px_0px_3px_rgba(228,29,29,0.3)]"
      >
        <span className="flex-shrink-0 w-2.5 h-2.5 rounded-full bg-white animate-ic-live-pulse motion-reduce:animate-none" />
        <span className="flex-1 min-w-0">Watch Rabbi Paltiel live (Live now)</span>
        <span className="flex-shrink-0 font-semibold underline">Join</span>
      </a>
    );
  }
  if (status.state === "soon") {
    return (
      <a
        href={paths.live()}
        className="bg-[rgba(228,29,29,0.08)] text-ic-error border border-[rgba(228,29,29,0.25)] rounded-xl px-4 py-3 min-h-[44px] flex items-center gap-2.5 text-[16px] font-semibold leading-6 no-underline box-border hover:no-underline hover:bg-[rgba(228,29,29,0.12)] focus-visible:outline-none focus-visible:shadow-[0px_0px_0px_3px_rgba(228,29,29,0.15)]"
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" className="flex-shrink-0">
          <circle cx="12" cy="12" r="9" />
          <path d="M12 7v5l3 2" />
        </svg>
        <span className="flex-1 min-w-0">{status.label || "Class starting in ~30 min"}</span>
        <span className="flex-shrink-0 underline">Details</span>
      </a>
    );
  }
  return (
    <a
      href={paths.live()}
      className="bg-white text-ic-body border border-ic-lightGray rounded-xl px-4 py-3 min-h-[44px] flex items-center gap-2.5 text-[16px] font-semibold leading-6 no-underline box-border shadow-ic-raised hover:no-underline hover:border-ic-borderGray hover:shadow-ic-elevated focus-visible:outline-none focus-visible:border-ic-blue focus-visible:shadow-ic-focus-soft"
    >
      <span className="flex-shrink-0 text-ic-blue"><VideoIcon size={18} /></span>
      <span className="flex-1 min-w-0">
        Watch Rabbi Paltiel live <span className="font-normal text-ic-slate">(Offline)</span>
      </span>
    </a>
  );
}

function UpcomingCard({ item }) {
  const featured = Boolean(item.featured);
  return (
    <Link
      to={item.href}
      onClick={() => {
        try {
          trackEvent({ category: "calendar", action: "coming_up_click", label: item.title });
        } catch (_) {}
      }}
      aria-label={`${item.title} — ${item.hook}`}
      className={`rounded-xl overflow-hidden no-underline hover:no-underline flex flex-col transition-all duration-150 ease-out focus-visible:outline-none ${
        featured
          ? "bg-ic-navy border border-white/10 shadow-ic-raised-dark hover:shadow-ic-elevated-dark focus-visible:shadow-ic-focus-dark"
          : "bg-white border border-ic-lightGray shadow-ic-raised hover:shadow-ic-elevated hover:border-ic-borderGray focus-visible:shadow-ic-focus-soft"
      }`}
      style={{
        flex: featured ? "0 0 clamp(288px, 78vw, 472px)" : "0 0 clamp(140px, 38vw, 232px)",
      }}
    >
      <div
        className={`relative ${item.image ? "bg-ic-navy" : "bg-ic-navyCard"} ${!item.image ? "flex items-center justify-center" : ""}`}
        style={{ height: featured ? "clamp(120px, 32vw, 190px)" : "clamp(80px, 21vw, 130px)" }}
      >
        {item.image ? (
          <img src={item.image} alt={`${item.title} banner`} className="w-full h-full object-cover block" />
        ) : (
          <span dir="rtl" className="font-extrabold text-ic-sky" style={{ fontSize: "clamp(22px, 6vw, 32px)", lineHeight: 1.2 }}>
            {item.lockupHebrew}
          </span>
        )}
        <span className={`absolute ${featured ? "top-2.5 left-2.5" : "top-2 left-2"}`}>
          {featured ? (
            <Badge variant="primary">FEATURED</Badge>
          ) : (
            <StatusChip kind={item.chip.kind} size="sm">{item.chip.label}</StatusChip>
          )}
        </span>
      </div>
      <div className={featured ? "px-4 py-3.5 flex flex-col gap-1" : "px-3 py-2.5 flex flex-col gap-0.5"}>
        {featured ? (
          <div className="flex items-center gap-2.5 flex-wrap">
            <span className="text-[20px] font-bold leading-7 text-white">{item.title}</span>
            <StatusChip kind={item.chip.kind} size="sm">{item.chip.label}</StatusChip>
          </div>
        ) : (
          <span className="text-[16px] font-semibold leading-6 text-black">{item.title}</span>
        )}
        <span className={`text-[${featured ? "14px" : "12px"}] ${featured ? "leading-5 text-ic-lightGray" : "leading-4 text-ic-slate"}`}>
          {item.hook}
        </span>
      </div>
    </Link>
  );
}

export default function RhythmRow() {
  const [today, setToday] = useState(null);
  const [parsha, setParsha] = useState(null);
  const live = useLiveStatus();
  const [upcoming, setUpcoming] = useState([]);
  const tanya = useDailyPlay("Tanya", paths.dailyTanya());
  const hayomYom = useDailyPlay("Hayom Yom", paths.dailyHayomYom());
  const dailyMeta = (d) => (d.playing ? "❚❚ Playing" : today ? `▶ ${today.hebrewDayMonth}` : "Daily study");

  useEffect(() => {
    getToday().then(setToday);
    getParsha().then(setParsha);
    getUpcoming().then(setUpcoming);
  }, []);

  return (
    <section className="max-w-[1280px] mx-auto px-5 pt-10 flex flex-col gap-3 w-full">
      <div className="flex items-baseline gap-3 flex-wrap">
        <Overline>Today</Overline>
        {today && (
          <span className="text-[14px] leading-5 text-ic-slate">
            {today.hebrew} · {today.civil}
          </span>
        )}
      </div>

      {/* Three across everywhere; the icons only join from tablet width up, where the tiles have room. */}
      <div className="grid grid-cols-3 gap-2">
        <DailyTile
          href={paths.dailyTanya()}
          kicker="Tanya"
          iconKey="tanya"
          title="Today's shiur"
          meta={dailyMeta(tanya)}
          onClick={tanya.onClick}
          ariaLabel={tanya.playing ? "Pause today's Tanya" : "Play today's Tanya"}
        />
        <DailyTile
          href={paths.dailyHayomYom()}
          kicker="Hayom Yom"
          iconKey="hayomyom"
          title="Today's entry"
          meta={dailyMeta(hayomYom)}
          onClick={hayomYom.onClick}
          ariaLabel={hayomYom.playing ? "Pause today's Hayom Yom" : "Play today's Hayom Yom"}
        />
        <DailyTile
          href={paths.weeklyParsha()}
          kicker="Parsha"
          iconKey="parsha"
          title={parsha?.name || "This week"}
          meta="This week's Torah portion"
          ariaLabel={`This week's Parsha${parsha ? ` — ${parsha.name}` : ""}`}
        />
      </div>

      <LiveSlot status={live} />

      <div className="flex flex-col gap-3 mt-4">
        <div className="flex items-baseline justify-between gap-3">
          <Overline>Coming Up</Overline>
          <Link
            to={paths.permalink("jewish-and-chassidic-calendar")}
            className="text-[14px] leading-5 text-ic-blue font-semibold py-3 px-2 -my-3 -mx-2 inline-flex items-center min-h-[44px] box-border hover:underline"
          >
            Full calendar
          </Link>
        </div>
        <Rail gap={12} className="pb-2">
          {upcoming.map((item) => (
            <UpcomingCard key={item.key} item={item} />
          ))}
        </Rail>
      </div>
    </section>
  );
}
